import { createApp, reactive } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import VueGoodTablePlugin from 'vue-good-table-next'
import 'vue-good-table-next/dist/vue-good-table-next.css'
import './scss/libs.scss'
import './js/theme.js'
import './scss/theme.scss'
import routes from './routes'
import { services } from './services'
import { EventBus } from "@/bus"
import miniToastr from 'mini-toastr'
import App from "./App.vue"
import filters from './filters'

// Notifications
const toastTypes = {
    success: 'success',
    error: 'error',
    info: 'info',
    warn: 'warn'
}
miniToastr.init({ types: toastTypes });
function toast({ title, message, type, timeout, cb }) {
    if (type == 'error')
        timeout = 100000000000;
    return miniToastr[type](message, title, timeout, cb)
}
const options = {
    success: toast,
    error: toast,
    info: toast,
    warn: toast
}
// End Notifications

function buildTitle (title) {
    document.title = `${document.title.split('|')[0]} | ${title}`
}

// SEO Helper functions
const defaultMeta = {
    description: 'Unofficial TikTok Private API. Fault-tolerant, read-only REST API for TikTok analytics: users, followers, videos, comments, hashtags and search. From $0.60 per 1,000 requests.',
    keywords: 'TikTok API, TikTok data, TikTok scraper, TikTok analytics, TikTok automation, social media API',
}

function updateMetaTag(name, content, isProperty = false) {
    const attr = isProperty ? 'property' : 'name'
    let element = document.querySelector(`meta[${attr}="${name}"]`)
    if (element) {
        element.setAttribute('content', content)
    } else {
        element = document.createElement('meta')
        element.setAttribute(attr, name)
        element.setAttribute('content', content)
        document.head.appendChild(element)
    }
}

function updateCanonicalUrl(path) {
    const baseUrl = `${env.PROTOCOL}://${env.DOMAIN}`
    const canonicalUrl = path === '/' ? baseUrl + '/' : baseUrl + path
    let link = document.querySelector('link[rel="canonical"]')
    if (link) {
        link.setAttribute('href', canonicalUrl)
    } else {
        link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        link.setAttribute('href', canonicalUrl)
        document.head.appendChild(link)
    }
    updateMetaTag('og:url', canonicalUrl, true)
}

function updateSEOMeta(meta, path) {
    const siteName = env.SITE_NAME || 'LamaTok'
    const description = meta.description || defaultMeta.description
    const keywords = meta.keywords || defaultMeta.keywords

    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)

    if (meta.noindex) {
        updateMetaTag('robots', 'noindex, nofollow')
    } else {
        updateMetaTag('robots', 'index, follow')
    }

    updateMetaTag('og:title', meta.title || siteName, true)
    updateMetaTag('og:description', description, true)
    updateMetaTag('og:type', 'website', true)
    updateMetaTag('og:site_name', siteName, true)

    updateMetaTag('twitter:title', meta.title || siteName)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:card', 'summary')

    updateCanonicalUrl(path)
}

window.getFilter = function(namespace, keyName, defaultValue) {
    let data = JSON.parse(localStorage.getItem(namespace + '_filter') || '{}');
    if (keyName) {
        return data[keyName] || defaultValue
    }
    return data
}
window.setFilter = function(namespace, data) {
    let current = window.getFilter(namespace);
    for (let key in data)
        current[key] = data[key];
    localStorage.setItem(namespace + '_filter', JSON.stringify(current));
    return true;
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    linkActiveClass: 'active',
    scrollBehavior (to, from, savedPosition) {
        return {left: 0, top: 0}
    }
})
const unsecuredCopyToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
        document.execCommand('copy');
    } catch(err) {
        console.error('Unable to copy to clipboard', err);
    }
    document.body.removeChild(textArea);
};
const app = createApp(App)
const fallbackProtocol = typeof window !== 'undefined' ? window.location.protocol.replace(':', '') : 'https'
const fallbackDomain = typeof window !== 'undefined' ? window.location.hostname : 'lamatok.com'

const globalState = reactive({
    context: {
        loaded: null,
    },
    rubRate: null,
    siteProto: env.PROTOCOL || fallbackProtocol,
    siteDomain: env.DOMAIN || fallbackDomain,
    siteName: env.SITE_NAME || 'LamaTok',
    siteEmail: env.SITE_EMAIL || 'support@lamatok.com',
    siteTgSupport: env.TG_SUPPORT || 'LamaTokSupportBot',
    siteTgBase: env.TG_BASE || 'https://telegram.me',
    footerCreds: env.FOOTER_CREDS ? JSON.parse(env.FOOTER_CREDS) : [],
    cfSiteKey: env.CF_SITE_KEY || '',
    capiUrl: "/api/",
})

const globalMethods = {
    copyToClipboard(content) {
        if (window.isSecureContext && navigator.clipboard) {
            navigator.clipboard.writeText(content);
        } else {
            unsecuredCopyToClipboard(content);
        }
        return true;
    },
    raise404() {
        router.push({ name: '404' });
    },
    raise500() {
        router.push({ name: '404' });
    },
    getContext() {
        services.Context().get().then(
            resp => globalMethods.updateContext(resp.body),
            _ => globalMethods.updateContext({})
        );
    },
    informContext() {
        EventBus.emit('context:loaded', globalState.context);
    },
    updateContext(context) {
        const deflt = {
            loaded: true,
            promocode: localStorage.getItem("promocode"),
            referrer: localStorage.getItem("referrer")
        }
        Object.assign(globalState.context, deflt, context);
        globalMethods.informContext();
    },
    buildTitle,
    updateSEOMeta(meta, path) {
        updateSEOMeta(meta, path);
        updateStructuredData(meta, path);
    },
}

function updateStructuredData(meta, path) {
    const baseUrl = `${env.PROTOCOL}://${env.DOMAIN}`
    const noindex = meta.noindex === true

    const graph = []

    // BreadcrumbList for non-root public pages
    if (!noindex && path !== '/') {
        const breadcrumbNames = { features: 'Features', faq: 'FAQ', status: 'Status', pricing: 'Pricing', review: 'Reviews', help: 'Help' }
        const segments = path.split('/').filter(Boolean)
        const items = [{ '@type': 'ListItem', position: 1, name: 'Home', item: `${baseUrl}/` }]
        let currentPath = ''
        segments.forEach((seg, i) => {
            currentPath += `/${seg}`
            items.push({ '@type': 'ListItem', position: i + 2, name: breadcrumbNames[seg] || meta.title || seg, item: `${baseUrl}${currentPath}` })
        })
        graph.push({ '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items })
    }

    // FAQPage schema is injected by SSR (see lm/steve/api/capi/routers/pages.py + PAGE_FAQ_DATA in config.py)

    if (graph.length === 0) {
        const existing = document.querySelector('script[data-seo-ld="dynamic"]')
        if (existing) existing.remove()
        return
    }

    let script = document.querySelector('script[data-seo-ld="dynamic"]')
    if (!script) {
        script = document.createElement('script')
        script.setAttribute('type', 'application/ld+json')
        script.setAttribute('data-seo-ld', 'dynamic')
        document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(graph)
}

router.beforeEach((to, from, next) => {
    buildTitle(to.meta.title || to.name)
    globalMethods.updateSEOMeta(to.meta || {}, to.path)
    next()
});

app.config.globalProperties.$state = globalState
app.config.globalProperties.$services = services
app.config.globalProperties.$methods = globalMethods
app.config.globalProperties.$filters = filters
app.config.globalProperties.$bus = EventBus
app.config.globalProperties.$notify = {
    success: (opts) => toast({ ...opts, type: 'success' }),
    error: (opts) => toast({ ...opts, type: 'error' }),
    info: (opts) => toast({ ...opts, type: 'info' }),
    warn: (opts) => toast({ ...opts, type: 'warn' }),
}
app.config.globalProperties.$linebreaks = filters.linebreaks
app.config.globalProperties.$percentage = filters.percentage

app.use(router)
app.use(VueGoodTablePlugin)
app.mount('#app')

globalMethods.getContext();

// Load RUB exchange rate
services.CurrencyRate().get().then(
    resp => { globalState.rubRate = resp.body.rate },
    _ => {}
);

EventBus.on('context:changed', globalMethods.getContext);
EventBus.on('context:updated', globalMethods.updateContext);
EventBus.on('context:request', globalMethods.informContext);
